import { ApiErrorResponse } from '@shared/types';

export interface ErrorTranslations {
  [key: string]: string | { [key: string]: string };
}

export function getErrorMessage(
  error: any,
  translations?: ErrorTranslations,
  fallbackMessage?: string
): string {
  const defaultFallback = 'An unexpected error occurred';
  
  if (!error) {
    return fallbackMessage || defaultFallback;
  }

  const errorData = error.response?.data as ApiErrorResponse;
  
  if (!errorData) {
    console.error('API Error (no response data):', error);
    return fallbackMessage || defaultFallback;
  }

  // Try to use errorCode with translations if available
  if (errorData.errorCode && translations) {
    const translatedMessage = translations[errorData.errorCode] || 
                             translations.api?.[errorData.errorCode];
    
    if (translatedMessage && typeof translatedMessage === 'string') {
      return translatedMessage;
    }
    
    console.warn(`Missing translation for error code: ${errorData.errorCode}`);
  }

  // Fall back to message from the error response
  if (errorData.message) {
    return errorData.message;
  }

  if (errorData.error) {
    return errorData.error;
  }

  console.error('API Error (unstructured):', errorData);
  return fallbackMessage || defaultFallback;
}

export function shouldLogError(errorCode?: string): boolean {
  const technicalErrors = [
    'INTERNAL_ERROR',
    'EXTERNAL_API_ERROR',
  ];
  
  return !errorCode || technicalErrors.includes(errorCode);
}

export function getErrorDetails(error: any): Record<string, any> | null {
  const errorData = error.response?.data as ApiErrorResponse;
  
  if (!errorData) {
    return null;
  }

  const { errorCode, message, error: errorMessage, ...details } = errorData;
  
  return Object.keys(details).length > 0 ? details : null;
}

export function isNetworkError(error: any): boolean {
  return !error.response && error.message === 'Network Error';
}

export function isAuthError(error: any): boolean {
  return error.response?.status === 401;
}

export function isNotFoundError(error: any): boolean {
  return error.response?.status === 404;
}

export function isValidationError(error: any): boolean {
  return error.response?.status === 400 || error.response?.status === 422;
}

export function isServerError(error: any): boolean {
  return error.response?.status >= 500;
}

export function apiErrorHandler(error: any): Error {
  const message = getErrorMessage(error);
  const err = new Error(message);
  (err as any).status = error.response?.status;
  (err as any).body = error.response?.data;
  return err;
}